export type GeolocationStatus = 'GRANTED' | 'DENIED' | 'UNAVAILABLE' | 'TIMEOUT' | 'NOT_SUPPORTED';

export interface AnticipationAuditTrail {
    browserHash?: string;
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    geolocationStatus: GeolocationStatus;
}

const GEOLOCATION_TIMEOUT_MS = 8000;

export type GeolocationPermission = 'granted' | 'prompt' | 'denied' | 'unsupported';

/**
 * Sem Permissions API (Safari antigo) nao ha como saber se ja foi concedida: trata como 'prompt',
 * o que no pior caso mostra a explicacao a quem ja tinha permitido.
 */
export const geolocationPermission = async (): Promise<GeolocationPermission> => {
    if (!navigator.geolocation) return 'unsupported';
    if (!navigator.permissions?.query) return 'prompt';

    try {
        return navigator.permissions
            .query({ name: 'geolocation' })
            .then((status): GeolocationPermission => (status.state === 'granted' || status.state === 'denied' ? status.state : 'prompt'))
            .catch((): GeolocationPermission => 'prompt');
    } catch {
        return 'prompt';
    }
};

const fingerprintSource = (): string =>
    [
        navigator.userAgent,
        navigator.language,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
        navigator.platform,
        String(navigator.hardwareConcurrency ?? ''),
    ].join('|');

export const browserHash = async (): Promise<string | undefined> => {
    if (!window.crypto?.subtle) return undefined;

    try {
        const digest = await Promise.resolve(window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(fingerprintSource())));
        return Array.from(new Uint8Array(digest))
            .map(byte => byte.toString(16).padStart(2, '0'))
            .join('');
    } catch {
        return undefined;
    }
};

type GeolocationResult = Pick<AnticipationAuditTrail, 'latitude' | 'longitude' | 'accuracy' | 'geolocationStatus'>;

/**
 * Nunca rejeita e nunca fica pendente: negar, ignorar ou fechar o prompt de localizacao nao pode
 * impedir a antecipacao. O `timeout` do getCurrentPosition so conta depois que o usuario responde
 * ao prompt -- sem o setTimeout proprio, um prompt ignorado deixa o botao carregando para sempre.
 */
export const geolocation = (timeoutMs: number = GEOLOCATION_TIMEOUT_MS): Promise<GeolocationResult> =>
    new Promise(resolve => {
        let settled = false;
        const settle = (result: GeolocationResult) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            resolve(result);
        };
        const timer = window.setTimeout(() => settle({ geolocationStatus: 'TIMEOUT' }), timeoutMs);

        if (!navigator.geolocation) {
            settle({ geolocationStatus: 'NOT_SUPPORTED' });
            return;
        }

        try {
            navigator.geolocation.getCurrentPosition(
                position =>
                    settle({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        geolocationStatus: 'GRANTED',
                    }),
                error => {
                    if (error.code === error.PERMISSION_DENIED) settle({ geolocationStatus: 'DENIED' });
                    else if (error.code === error.TIMEOUT) settle({ geolocationStatus: 'TIMEOUT' });
                    else settle({ geolocationStatus: 'UNAVAILABLE' });
                },
                { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
            );
        } catch {
            settle({ geolocationStatus: 'UNAVAILABLE' });
        }
    });

export const collectAuditTrail = async (): Promise<AnticipationAuditTrail> => {
    const pendingHash = browserHash();
    const position = await geolocation();

    return { ...position, browserHash: await pendingHash };
};
